import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { ratesAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    Save, 
    Upload, 
    Download,
    Loader2,
    History
} from 'lucide-react';

export const AdminRates = () => {
    const navigate = useNavigate();
    const [rates, setRates] = useState([]);
    const [version, setVersion] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchRates();
    }, []);

    const fetchRates = async () => {
        try {
            const response = await ratesAPI.get();
            setRates(response.data.rates || []);
            setVersion(response.data.version || 'default');
        } catch (error) {
            console.error('Failed to fetch rates:', error);
            toast.error('Failed to load rates');
        } finally {
            setLoading(false);
        }
    };

    const isSplitRate = (key) =>
        key.includes('_gbp_') || key === 'minimum_charge_gbp';

    const handleRateChange = (key, field, value) => {
        setRates(rates.map(r => {
            if (r.key !== key) return r;
            const parsed = parseFloat(value) || 0;
            if (field === 'value') return { ...r, value: parsed };
            const updated = { ...r, [field]: parsed };
            updated.value = (field === 'labour' ? parsed : (r.labour || 0)) +
                            (field === 'materials' ? parsed : (r.materials || 0));
            return updated;
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await ratesAPI.update({ rates });
            toast.success('Rates saved! New version created.');
            fetchRates();
        } catch (error) {
            console.error('Failed to save rates:', error);
            toast.error('Failed to save rates');
        } finally {
            setSaving(false);
        }
    };

    const handleExport = async () => {
        try {
            const response = await ratesAPI.export();
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'rates.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Rates exported!');
        } catch (error) {
            toast.error('Failed to export rates');
        }
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                await ratesAPI.import(event.target?.result);
                toast.success('Rates imported successfully!');
                fetchRates();
            } catch (error) {
                console.error('Import error:', error);
                toast.error(error.response?.data?.detail || 'Failed to import rates');
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const formatKey = (key) => {
        // Friendly display names for rendering rates
        const friendlyNames = {
            'renderSandCement_rate_gbp_per_m2': 'Sand & Cement Render',
            'renderSilicone_rate_gbp_per_m2': 'Silicone Render',
            'renderMonocouche_rate_gbp_per_m2': 'Monocouche Render',
            'renderKRend_rate_gbp_per_m2': 'K-Rend',
            'renderTyrolean_rate_gbp_per_m2': 'Tyrolean / Dry Dash',
        };
        if (friendlyNames[key]) return friendlyNames[key];
        return key
            .replace(/_/g, ' ')
            .replace(/gbp/gi, '(£)')
            .replace(/percent/gi, '(%)')
            .replace(/m2/gi, '(m²)')
            .replace(/per/gi, '/')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-4">
                    <Skeleton className="h-8 w-48" />
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton key={i} className="h-16 rounded-lg" />
                    ))}
                </div>
            </Layout>
        );
    }

    // Group rates by category
    const rateGroups = {
        'Measurement & Deductions': rates.filter(r => 
            r.key.includes('Deduction') || r.key.includes('waste') || r.key.includes('minimum') || r.key.includes('validity')
        ),
        'Prep Uplifts': rates.filter(r => r.key.includes('prep_uplift')),
        'Core Plastering': rates.filter(r => 
            r.key.includes('skim') || r.key.includes('patching') || r.key.includes('bonding') || 
            r.key.includes('beads') || r.key.includes('scrim') || r.key.includes('remove')
        ),
        'Boarding': rates.filter(r => r.key.includes('board') || r.key.includes('overboard')),
        'Rendering': rates.filter(r => r.key.startsWith('render')),
        'Other': rates.filter(r => 
            r.key.includes('setUp') || r.key.includes('wasteDisposal')
        ),
        'VAT Settings': rates.filter(r => r.key.includes('vat'))
    };

    return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate('/settings')}
                        className="h-10 w-10"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
                            Rate Card
                        </h1>
                        <p className="text-slate-500 text-sm">Version: {version}</p>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={handleExport}
                        className="flex-1 h-10"
                        data-testid="export-rates-btn"
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Export CSV
                    </Button>
                    <label className="flex-1">
                        <Button
                            variant="outline"
                            className="w-full h-10"
                            asChild
                        >
                            <span>
                                <Upload className="h-4 w-4 mr-2" />
                                Import CSV
                            </span>
                        </Button>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleImport}
                            className="hidden"
                            data-testid="import-rates-input"
                        />
                    </label>
                </div>

                {/* Rate Groups */}
                {Object.entries(rateGroups).map(([groupName, groupRates]) => (
                    groupRates.length > 0 && (
                        <Card key={groupName} className="bg-white border-slate-200 shadow-sm">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-heading text-sm uppercase tracking-tight text-slate-600">
                                    {groupName}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {groupRates.map((rate) => (
                                    <div key={rate.key}>
                                        <Label className="text-sm font-medium text-slate-700 block mb-1.5 truncate">
                                            {formatKey(rate.key)}
                                        </Label>
                                        {isSplitRate(rate.key) ? (
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1">
                                                    <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Labour (£)</p>
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={rate.labour || 0}
                                                        onChange={(e) => handleRateChange(rate.key, 'labour', e.target.value)}
                                                        className="h-10 text-right border-slate-300 border-blue-200 focus:border-blue-400"
                                                        data-testid={`rate-labour-${rate.key}`}
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Materials (£)</p>
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={rate.materials || 0}
                                                        onChange={(e) => handleRateChange(rate.key, 'materials', e.target.value)}
                                                        className="h-10 text-right border-slate-300 border-orange-200 focus:border-orange-400"
                                                        data-testid={`rate-materials-${rate.key}`}
                                                    />
                                                </div>
                                                <div className="w-24 shrink-0">
                                                    <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Total (£)</p>
                                                    <div className="h-10 px-3 flex items-center bg-slate-50 border border-slate-200 rounded-md text-right font-semibold text-slate-700 text-sm tabular-nums justify-end">
                                                        {((rate.labour || 0) + (rate.materials || 0)).toFixed(2)}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1" />
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={rate.value}
                                                    onChange={(e) => handleRateChange(rate.key, 'value', e.target.value)}
                                                    className="w-28 h-10 text-right border-slate-300"
                                                    data-testid={`rate-${rate.key}`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )
                ))}

                {/* Save Button */}
                <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide"
                    data-testid="save-rates-btn"
                >
                    {saving ? (
                        <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Save className="mr-2 h-5 w-5" />
                            Save & Create New Version
                        </>
                    )}
                </Button>
            </div>
        </Layout>
    );
};
